import Modal from "@/common/elements/Modal";
import { StyledGridCol, StyledGridRow, StyledFlex } from "@/common/styles";
import QuickSearchBookings from "@/components/SearchForms/QuickSearchBookings";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import {
  buildLineUpWhereClauseStructure,
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import { Form, message } from "antd";
import Select from "@/common/elements/InputElements/Select";
import dayjs from "dayjs";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { useEffect, useState } from "react";
import Button from "@/common/elements/Button";
import { MODULES_LIST } from "@/util/common_variable";
import { useDispatch } from "react-redux";
import { setWhereClause } from "@/slices/lineupsSlice";
import useDialog from "@/hooks/useDialog";

const BookingsFilter = ({
  data = null,
  isFindModalVisible,
  setIsFindModalVisible,
  onHandleCancel,
  setReportListData,
  setReportListTotal,
  setBookingSearchPayload,
  setPagination,
  setIsFiltered,
  apiModule = MODULES_LIST.BOOKINGS,
  path = MODULES_LIST.STAFF,
  resetSignal,
  setReportListLoading,
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [searchForm] = Form.useForm();
  const [isLoading, setIsloading] = useState(false);

  useEffect(() => {
    searchForm.resetFields();
  }, [isFindModalVisible]);

  useEffect(() => {
    if (resetSignal) {
      searchForm.resetFields();
    }
  }, [resetSignal]);

  useEffect(() => {
    if (isNonEmptyVariable(data)) {
      searchForm.setFieldsValue({
        ...data,
        heightPlusOrMinus: 2,
        weightPlusOrMinus: 5,
        agePlusOrMinus: 2,
        imageType: "Mug-Front",
      });
    }
  }, [data]);

  const fetchSearchResults = async () => {
    try {
      const values = await searchForm.validateFields();

      const filteredEntries = Object.entries(values).filter(([key, value]) => {
        if (dayjs.isDayjs(value)) {
          return value;
        }
        return value !== undefined && value !== null && value !== "";
      });

      const mapPayload = filteredEntries.map(([key, value]) => {
        const formattedValue = dayjs.isDayjs(value)
          ? value.format("MM/DD/YYYY")
          : value;

        if (
          [
            "height",
            "weight",
            "ageAtEntry",
            "heightPlusOrMinus",
            "weightPlusOrMinus",
            "agePlusOrMinus",
          ].includes(key)
        ) {
          return { filterKey: key, value: Number(value) };
        }

        if (key === "juvenile") {
          if (value === "No Juveniles") {
            return { filterKey: key, value: 0 };
          } else if (value === "Only Juveniles") {
            return { filterKey: key, value: 1 };
          }
        }

        // Map requiresReview checkbox to numeric value expected by API
        if (key === "requiresReview") {
          return { filterKey: key, value: value ? 1 : 0 };
        }

        return { filterKey: key, value: formattedValue };
      });

      const isActiveItem = mapPayload.find(
        (item) => item.filterKey === "isActive",
      );

      let payload;
      if (apiModule === MODULES_LIST.STAFF) {
        payload = [
          ...mapPayload.filter((item) => item.filterKey !== "isActive"),
          {
            filterKey: "isActive",
            value: isActiveItem ? !isActiveItem.value : true,
          },
        ];
      } else {
        payload = mapPayload;
      }

      // Always ensure searchType is present in the payload
      const hasSearchType = payload.some((item) => item.filterKey === "searchType");
      if (!hasSearchType) {
        payload = [{ filterKey: "searchType", value: "Starts With" }, ...payload];
      }

      if (path !== MODULES_LIST.LINEUPS) setBookingSearchPayload(payload);

      // ── Resolve URL based on module ──────────────────────────────────
      let url = API_ENDPOINTS.bookingsSearch + "?size=100&direction=asc";

      if (apiModule === MODULES_LIST.STAFF) {
        url = API_ENDPOINTS.staff.searchStaff + "?size=100&direction=asc";
      } else if (apiModule === MODULES_LIST.LINEUPS) {
        url = API_ENDPOINTS.lineups.listPhotos + "?size=100&direction=asc";
        const lineupWhereClause = buildLineUpWhereClauseStructure(payload);
        dispatch(setWhereClause(lineupWhereClause));
      } else if (apiModule === MODULES_LIST.WARRANTS) {
        url =
          API_ENDPOINTS.warrants.searchWarrants +
          "?size=100&direction=asc&orderBy=warrantRootID";
      }

      const response = await api.post(url, payload);
      return response.data;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const onHandelFindGo = async (action) => {
    try {
      setIsloading(true);
      if (action !== "count") setReportListLoading?.(true);

      if (path !== MODULES_LIST.LINEUPS) {
        setPagination({ pageIndex: 0, pageSize: 100 });
      }

      const data = await fetchSearchResults();

      if (!isFindModalVisible && !isNonEmptyVariable(data)) {
        return;
      }

      if (isNonEmptyVariable(data)) {
        // ── Count action ─────────────────────────────────────────────
        if (action === "count") {
          // Lineups response: data.filteredRecords.total
          // Everything else (Bookings, Warrants, Staff): data.total
          const total =
            apiModule === MODULES_LIST.LINEUPS
              ? data?.filteredRecords?.total
              : data?.total;

          showDialog("info", {
            title: "Count",
            content: `${total} records found`,
            confirmButtonText: "Ok",
          });
          return;
        }

        // ── Staff ────────────────────────────────────────────────────
        if (apiModule === MODULES_LIST.STAFF) {
          const rowData = data.rows;
          if (isNonEmptyVariable(rowData)) {
            const rowData2 = rowData.map((a) => ({
              staffId:     a.staffID,
              name:        a?.name,
              badgeNumber: a?.badgeNumber,
              rank:        a?.rank,
              division:    a?.division,
              username:    a.username,
              agency:      a?.agency,
            }));

            setReportListData((prev) => ({ ...prev, rows: rowData2 }));
            setReportListTotal(data?.total);
            setIsFiltered(true);
          }

        // ── Lineups ──────────────────────────────────────────────────
        } else if (apiModule === MODULES_LIST.LINEUPS) {
          const aldata = data?.filteredRecords?.rows?.map((r) => ({
            ...r,
            visited: false,
          }));
          setReportListData(() => aldata);
          setReportListTotal(data?.totalRecords);

        // ── Warrants ─────────────────────────────────────────────────
        } else if (apiModule === MODULES_LIST.WARRANTS) {
          const statusOptions = getOptionsFromRefList("Status", false, false, true);

          const rowData = (data?.rows ?? []).map((a, i) => {
            const statusOption = statusOptions.find((o) => o.value === a.statusId);
            return {
              _rowKey:       i,
              warrantRootID: a.warrantRootID,
              warrantNumber: a.warrantNumber ?? "",
              lName:         a.lName         ?? "",
              fName:         a.fName         ?? "",
              mName:         a.mName         ?? "",
              statusId:      statusOption?.label ?? "",
              dob:           a.dob           ?? "",
            };
          });

          setReportListData(rowData);
          setReportListTotal(data?.total ?? 0);
          setIsFiltered(true);

        // ── Bookings (default) ───────────────────────────────────────
        } else {
          setReportListData(() => data?.rows);
          setReportListTotal(data?.total);
          setIsFiltered(true);
        }

        setIsFindModalVisible(false);
      }

      message.success("Search completed successfully");
    } catch (err) {
      console.log(err);
      setReportListData([]);
      message.error("Search failed");
    } finally {
      setIsloading(false);
      if (action !== "count") setReportListLoading?.(false);
    }
  };

  return (
    <Modal
      show={isFindModalVisible}
      size="lg"
      width="62%"
      header="Quick Search"
      designPattern="ant"
      maskClosable={false}
      onHide={onHandleCancel}
      handleCancel={onHandleCancel}
      footerActionsNeed={false}
    >
      <Form
        form={searchForm}
        layout="vertical"
        initialValues={{ searchType: "Starts With" }}
      >
        <QuickSearchBookings path={path} />

        <StyledGridRow
          className="ant-modal-footer custom"
          justify="space-between"
        >
          <StyledGridCol>
            <FormItem label="Search Type" layout="horizontal" name="searchType">
              <Select
                options={getOptionsFromRefList("Search_Type")}
                style={{ minWidth: "148px" }}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol>
            <StyledFlex gap="10px">
              {/* Count button — shown for Lineups, Warrants, and Bookings */}
              {(path === MODULES_LIST.LINEUPS ||
                path === MODULES_LIST.WARRANTS ||
                path === MODULES_LIST.BOOKINGS) && (
                <Button
                  onClick={() => onHandelFindGo("count")}
                  loading={isLoading}
                >
                  Count
                </Button>
              )}

              <Button
                color="default"
                type="default"
                onClick={() => onHandelFindGo("go")}
                loading={!setReportListLoading && isLoading}
                disabled={isLoading}
              >
                Go
              </Button>
              <Button color="default" type="default" onClick={onHandleCancel}>
                Cancel
              </Button>
            </StyledFlex>
          </StyledGridCol>
        </StyledGridRow>
      </Form>
    </Modal>
  );
};

export default BookingsFilter;
